import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import toast from 'react-hot-toast';

interface Customer { id: string; name: string; whatsapp?: string; email?: string; }
interface Quote {
  id: string; jobScope: string; totalAmount: number; status: string;
  createdAt: string; pdfUrl?: string; clientName?: string;
  quoteText?: string; customer?: Customer; sentViaWA?: boolean; sentViaEmail?: boolean;
}

const fmt = (c: number) => `$${(c / 100).toFixed(2)}`;
const statusColor = (s: string): any =>
  s === 'won'  ? { background: '#dcfce7', color: '#16a34a' } :
  s === 'sent' ? { background: '#dbeafe', color: '#1d4ed8' } :
                 { background: '#f3f4f6', color: '#555' };

export default function Quotes() {
  const [quotes,    setQuotes]    = useState<Quote[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [expanded,  setExpanded]  = useState<string | null>(null);
  const [editing,   setEditing]   = useState<string | null>(null);
  const [editText,  setEditText]  = useState('');
  const [sending,   setSending]   = useState<string | null>(null);
  const [sendModal, setSendModal] = useState<Quote | null>(null);
  // New customer form inside send modal
  const [newCust, setNewCust] = useState({ name: '', whatsapp: '', email: '' });
  const [savingCust, setSavingCust] = useState(false);

  const load = () => {
    api.get('/quotes').then(r => setQuotes(r.data));
    api.get('/customers').then(r => setCustomers(r.data));
  };

  useEffect(() => { load(); }, []);

  const markWon = async (id: string) => {
    await api.put(`/quotes/${id}`, { status: 'won' });
    setQuotes(q => q.map(x => x.id === id ? { ...x, status: 'won' } : x));
    toast.success('Marked as won! 🎉');
  };

  const del = async (id: string) => {
    if (!window.confirm('Delete this quote?')) return;
    await api.delete(`/quotes/${id}`);
    setQuotes(q => q.filter(x => x.id !== id));
    toast.success('Deleted');
  };

  const startEdit = (q: Quote) => {
    setEditing(q.id);
    setEditText(q.quoteText || '');
    setExpanded(q.id);
  };

  const saveEdit = async (q: Quote) => {
    try {
      const { data } = await api.put(`/quotes/${q.id}/text`, {
        quoteText:  editText,
        clientName: q.clientName,
        customerId: q.customer?.id
      });
      setQuotes(prev => prev.map(x => x.id === q.id ? { ...x, ...data } : x));
      setEditing(null);
      toast.success('Quote updated!');
    } catch { toast.error('Failed to save'); }
  };

  const saveNewCustomer = async () => {
    if (!newCust.name) return toast.error('Name is required');
    setSavingCust(true);
    try {
      const { data } = await api.post('/customers', newCust);
      setCustomers(prev => [...prev, data]);
      // Link to the quote
      if (sendModal) {
        await api.put(`/quotes/${sendModal.id}/text`, {
          quoteText:  sendModal.quoteText,
          clientName: data.name,
          customerId: data.id
        });
        setSendModal({ ...sendModal, customer: data, clientName: data.name });
        setQuotes(q => q.map(x => x.id === sendModal.id ? { ...x, customer: data, clientName: data.name } : x));
      }
      setNewCust({ name: '', whatsapp: '', email: '' });
      toast.success('Customer saved!');
    } catch { toast.error('Failed to save customer'); }
    finally { setSavingCust(false); }
  };

  const linkCustomer = async (quote: Quote, customerId: string) => {
    const cust = customers.find(c => c.id === customerId);
    await api.put(`/quotes/${quote.id}/text`, {
      quoteText:  quote.quoteText,
      clientName: cust?.name || quote.clientName,
      customerId
    });
    setQuotes(q => q.map(x => x.id === quote.id ? { ...x, customer: cust, clientName: cust?.name } : x));
    if (sendModal?.id === quote.id) setSendModal(prev => prev ? { ...prev, customer: cust, clientName: cust?.name } : prev);
    toast.success('Customer linked!');
  };

  const sendQuote = async (quote: Quote, via: string) => {
    if (!quote.customer) return toast.error('Link a customer first');
    setSending(quote.id + via);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/send`, { via });
      const msgs = [];
      if (data.results.whatsapp) msgs.push('WhatsApp ✅');
      if (data.results.email)    msgs.push('Email ✅');
      if (msgs.length)  toast.success(`Sent via ${msgs.join(' & ')}`);
      else              toast.error('Could not send — check customer contact details');
      setQuotes(q => q.map(x => x.id === quote.id ? { ...x, status: 'sent' } : x));
      setSendModal(null);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Send failed');
    } finally { setSending(null); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6' }}>
      <nav style={{ background: 'white', padding: '16px 24px', display: 'flex', gap: 24, alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <Link to="/dashboard" style={{ color: '#2563EB', fontWeight: 700, textDecoration: 'none' }}>← Dashboard</Link>
        <span style={{ fontWeight: 700 }}>All Quotes</span>
      </nav>

      <div style={{ maxWidth: 900, margin: '32px auto', padding: '0 24px' }}>
        {quotes.length === 0 ? (
          <div style={{ background: 'white', borderRadius: 12, padding: 48, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <p style={{ color: '#999', marginBottom: 16 }}>No quotes yet.</p>
            <Link to="/dashboard" style={{ color: '#2563EB' }}>Generate your first quote →</Link>
          </div>
        ) : quotes.map(q => (
          <div key={q.id} style={{ background: 'white', borderRadius: 12, marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>

            {/* Quote row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px' }}>
              <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setExpanded(expanded === q.id ? null : q.id)}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {q.jobScope.substring(0, 65)}{q.jobScope.length > 65 ? '...' : ''}
                </div>
                <div style={{ fontSize: 13, color: '#999', display: 'flex', gap: 12 }}>
                  <span>{new Date(q.createdAt).toLocaleDateString()}</span>
                  {q.customer
                    ? <span style={{ color: '#2563EB' }}>👤 {q.customer.name}</span>
                    : <span style={{ color: '#f59e0b' }}>No customer linked</span>
                  }
                  {q.sentViaWA    && <span style={{ color: '#16a34a' }}>📱 WhatsApp</span>}
                  {q.sentViaEmail && <span style={{ color: '#16a34a' }}>📧 Email</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, marginLeft: 12 }}>
                <span style={{ fontWeight: 700, color: '#2563EB', fontSize: 16 }}>{fmt(q.totalAmount)}</span>
                <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 12, ...statusColor(q.status) }}>{q.status}</span>

                {/* Send button */}
                <button onClick={() => setSendModal(q)}
                  style={{ fontSize: 12, padding: '5px 10px', background: '#2563EB', border: 'none', borderRadius: 6, cursor: 'pointer', color: 'white', fontWeight: 600 }}>
                  Send
                </button>

                {/* Edit button */}
                <button onClick={() => startEdit(q)}
                  style={{ fontSize: 12, padding: '5px 10px', background: '#f3f4f6', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#555', fontWeight: 600 }}>
                  Edit
                </button>

                {q.pdfUrl && q.pdfUrl.startsWith('http') && (
                  <a href={q.pdfUrl} target="_blank" rel="noreferrer"
                    style={{ fontSize: 12, padding: '5px 10px', background: '#dcfce7', borderRadius: 6, color: '#16a34a', textDecoration: 'none', fontWeight: 600 }}>
                    PDF
                  </a>
                )}

                {q.status !== 'won' && (
                  <button onClick={() => markWon(q.id)}
                    style={{ fontSize: 12, padding: '5px 10px', background: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#16a34a' }}>Won</button>
                )}
                <button onClick={() => del(q.id)}
                  style={{ fontSize: 12, padding: '5px 10px', background: '#fee2e2', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#dc2626' }}>✕</button>
              </div>
            </div>

            {/* Expanded: edit or view quote text */}
            {expanded === q.id && (
              <div style={{ padding: '0 20px 20px', borderTop: '1px solid #f0f0f0' }}>
                {editing === q.id ? (
                  <>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: '#374151', marginTop: 16 }}>
                      Edit Quote Text
                    </label>
                    <textarea
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      style={{ width: '100%', height: 200, padding: 12, border: '1px solid #ddd', borderRadius: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'monospace' }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button onClick={() => saveEdit(q)}
                        style={{ padding: '8px 18px', background: '#2563EB', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                        Save & Regenerate PDF
                      </button>
                      <button onClick={() => setEditing(null)}
                        style={{ padding: '8px 18px', background: '#f3f4f6', color: '#555', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <pre style={{ marginTop: 16, fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', fontFamily: 'monospace', background: '#f9fafb', padding: 16, borderRadius: 8 }}>
                    {q.quoteText || 'No quote text'}
                  </pre>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Send Modal ── */}
      {sendModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 32, width: 480, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ marginBottom: 4 }}>Send Quote</h3>
            <p style={{ color: '#666', fontSize: 13, marginBottom: 24 }}>
              Total: <strong>{fmt(sendModal.totalAmount)}</strong>
            </p>

            {/* Customer section */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 14 }}>
                Customer
              </label>

              {sendModal.customer ? (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{sendModal.customer.name}</div>
                    <div style={{ fontSize: 13, color: '#666' }}>
                      {sendModal.customer.whatsapp && `📱 ${sendModal.customer.whatsapp}`}
                      {sendModal.customer.whatsapp && sendModal.customer.email && '  '}
                      {sendModal.customer.email && `📧 ${sendModal.customer.email}`}
                    </div>
                  </div>
                  <button onClick={() => setSendModal({ ...sendModal, customer: undefined })}
                    style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: 18 }}>✕</button>
                </div>
              ) : (
                <>
                  {/* Pick existing customer */}
                  {customers.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <select
                        defaultValue=""
                        onChange={e => e.target.value && linkCustomer(sendModal, e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14 }}>
                        <option value="">— Select existing customer —</option>
                        {customers.map(c => (
                          <option key={c.id} value={c.id}>{c.name} {c.whatsapp ? `(${c.whatsapp})` : c.email ? `(${c.email})` : ''}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Or add new */}
                  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: '#374151' }}>+ Add New Customer</div>
                    <input
                      value={newCust.name}
                      onChange={e => setNewCust(n => ({ ...n, name: e.target.value }))}
                      placeholder="Customer name *"
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, marginBottom: 8, boxSizing: 'border-box' }}
                    />
                    <input
                      value={newCust.whatsapp}
                      onChange={e => setNewCust(n => ({ ...n, whatsapp: e.target.value }))}
                      placeholder="WhatsApp number (e.g. +8801712345678)"
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, marginBottom: 8, boxSizing: 'border-box' }}
                    />
                    <input
                      value={newCust.email}
                      onChange={e => setNewCust(n => ({ ...n, email: e.target.value }))}
                      placeholder="Email address"
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, marginBottom: 12, boxSizing: 'border-box' }}
                    />
                    <button onClick={saveNewCustomer} disabled={savingCust}
                      style={{ padding: '9px 18px', background: '#2563EB', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                      {savingCust ? 'Saving...' : 'Save Customer'}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Send buttons */}
            {sendModal.customer && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                {sendModal.customer.whatsapp && (
                  <button
                    onClick={() => sendQuote(sendModal, 'whatsapp')}
                    disabled={!!sending}
                    style={{ padding: '12px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 15 }}>
                    {sending === sendModal.id + 'whatsapp' ? 'Sending...' : '📱 Send via WhatsApp'}
                  </button>
                )}
                {sendModal.customer.email && (
                  <button
                    onClick={() => sendQuote(sendModal, 'email')}
                    disabled={!!sending}
                    style={{ padding: '12px', background: '#2563EB', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 15 }}>
                    {sending === sendModal.id + 'email' ? 'Sending...' : '📧 Send via Email'}
                  </button>
                )}
                {sendModal.customer.whatsapp && sendModal.customer.email && (
                  <button
                    onClick={() => sendQuote(sendModal, 'both')}
                    disabled={!!sending}
                    style={{ padding: '12px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 15 }}>
                    {sending === sendModal.id + 'both' ? 'Sending...' : '🚀 Send via Both'}
                  </button>
                )}
              </div>
            )}

            <button onClick={() => setSendModal(null)}
              style={{ width: '100%', padding: 12, background: '#f3f4f6', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, color: '#555' }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}